import logger from '@/providers/Logging';

/**
 * Configuration constants for TUS fingerprint cleanup
 */
export const DEFAULT_FINGERPRINT_MAX_AGE_DAYS = 7;
export const LOGOUT_FINGERPRINT_MAX_AGE_DAYS = 1;

/**
 * Options for clearing TUS fingerprints
 */
type ClearTusFingerprintsOptions = {
  /**
   * Maximum age in days before a fingerprint is considered stale.
   * Set to 0 to clear all fingerprints regardless of age.
   */
  maxAgeDays: number;
  /**
   * If true, automatically retry with maxAgeDays = 0 if the age-based cleanup fails.
   */
  clearAllOnError: boolean;
};

/**
 * Removes stale TUS upload fingerprints from localStorage based on their age.
 *
 * TUS fingerprints are stored with keys like: tus::${endpoint}::${fingerprint}
 * Each value is a JSON object containing a creationTime field.
 *
 * This function:
 * - Parses each tus::* localStorage entry
 * - Extracts the creationTime field
 * - Removes fingerprints older than the specified threshold
 * - Removes malformed entries (invalid JSON, missing creationTime, etc.)
 * - If maxAgeDays is 0, clears all fingerprints immediately
 * - If clearAllOnError is true and cleanup fails, retries by clearing all fingerprints
 *
 * @param options - Configuration options for cleanup behavior
 */
export const clearStaleTusFingerprints = (options: ClearTusFingerprintsOptions): void => {
  const { maxAgeDays, clearAllOnError } = options;

  try {
    const keysToRemove: string[] = [];
    const now = Date.now();
    const maxAgeMilliseconds = maxAgeDays * 24 * 60 * 60 * 1000;
    const clearAll = maxAgeDays === 0;

    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);

      if (key?.startsWith('tus::')) {
        // If clearing all, skip age checks and just queue for removal
        if (clearAll) {
          keysToRemove.push(key);
        } else {
          try {
            const value = localStorage.getItem(key);

            if (!value) {
              // Empty value, remove it
              keysToRemove.push(key);
            } else {
              // Parse the fingerprint data
              const fingerprintData = JSON.parse(value);

              if (!fingerprintData.creationTime) {
                // Missing creationTime field, treat as stale and remove
                keysToRemove.push(key);
              } else {
                // Parse the creation date
                const creationDate = new Date(fingerprintData.creationTime);

                // Check if date is valid
                if (Number.isNaN(creationDate.getTime())) {
                  // Invalid date, remove the entry
                  keysToRemove.push(key);
                } else {
                  // Calculate age in milliseconds
                  const age = now - creationDate.getTime();

                  // Remove if older than threshold
                  if (age > maxAgeMilliseconds) {
                    keysToRemove.push(key);
                  }
                }
              }
            }
          } catch (error) {
            // Failed to parse JSON or process entry, remove it
            logger.warn(`Failed to parse TUS fingerprint for key ${key}`, { error, key });
            keysToRemove.push(key);
          }
        }
      }
    }

    // Remove all identified stale fingerprints
    keysToRemove.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        logger.error(`Failed to remove TUS fingerprint ${key}`, { error, key });
      }
    });

    if (keysToRemove.length > 0) {
      logger.info(`Cleaned up ${keysToRemove.length} stale TUS fingerprint(s)`, {
        count: keysToRemove.length,
      });
    }
  } catch (error) {
    // localStorage might not be available in some contexts (SSR, private browsing, etc)
    logger.error('Failed to clear stale TUS fingerprints', { clearAllOnError, error, maxAgeDays });

    // If clearAllOnError is true and we're not already clearing all, retry with maxAgeDays = 0
    if (clearAllOnError && maxAgeDays !== 0) {
      logger.info('Retrying with clear all mode...');
      clearStaleTusFingerprints({ clearAllOnError: false, maxAgeDays: 0 });
    }
  }
};
